// Copyright (c) 2020-2024 Visual Intelligence LP. All rights reserved.
//
import qs, { IParseOptions } from 'qs';
import { LocationDescriptorObject } from 'history';
import omitBy from 'lodash/omitBy';
import moment from 'moment';

import history from '@shared/utils/history';
import { Queries } from '@shared/types/common';
import { DayStatus } from '@shared/constants/common';
import { CATALOG_GRID_COLLAPSED_ITEMS, MAP_SETTINGS, SHIPPING_GRID_COLLAPSED_ITEMS, SITES_GRID_COLLAPSED_ITEMS, STATUS_GRID_COLLAPSED_ITEMS, SUMMARY_GRID_COLLAPSED_ITEMS, TEAM_GRID_COLLAPSED_ITEMS } from "@shared/constants/sp-sheets";
import { AddressParts, SDeliverableInfoDTO } from '@shared/models/sp-sheets';
import { DeliverableDTO } from '@shared/models/deliverable';
import { DeliverablesStore } from '@shared/stores';
import { DimensionDTO } from '@shared/models/deliverable/dimension';
import { ASSET_CONSTRUCTION_TYPE_LABELS } from '@shared/constants/asset';
import { VirtualNode } from '@shared/models/document-repo';
import { TaskTreeItemDTO } from '@shared/models/deliverable/task-tree-item';
import { OrderTask, TaskNode } from '@shared/models/ordertask';
import { DeliverablesSummary, ScheduleBarSegment, ScheduleNode, ScheduleSiteInfo, STAGE_IDX, StageId2Name } from '@shared/models/schedule';
import { round } from 'lodash';
import { OrderAssetDTO } from '@shared/models/order';

export const inchesPerMm = 0.0393701;
export const feetPerM = 3.280839895;

// Deep equal with modifications to standard comparison
// zero and space are equal
// null and space are equal
// '1' and 1 are equal
export const deepEqual = (x, y)  => {
  if (x == y) {
    return true;
  }
  else if ((typeof x == "object" && x != null) && (typeof y == "object" && y != null)) {
    if (Object.prototype.toString.call(x) === '[object Date]') {
      if (Object.prototype.toString.call(y) === '[object Date]') {
        return x.toString() == y.toString();
      }
      return false;
    }
    if (Object.keys(x).length != Object.keys(y).length)
    {  
      //console.log('deepEqual different length', Object.keys(x), Object.keys(y));
      return false;
    }
    for (const prop in x) {
      if (y.hasOwnProperty(prop))
      {  
        if (! deepEqual(x[prop], y[prop]))
        {
          //console.log('deepEqual ', x[prop], y[prop])
          return false;
        }
      }
      else
      {
        //console.log('deepEqual y.hasOwnProperty == false');
        return false;
      }
    }
    return true;
  }
  else if ((x == null && y == '') || (y == null && x == ''))
  {
    return true;
  }
  else if ((x == null && y == 'Invalid Date') || (y == null && x == 'Invalid Date'))
  {
    return true;
  }
  else 
  {
    //console.log('deepEqual different types');
    return false;
  }
}

// Find min and max for a dictionary with number value.
export const getMaxMinInDict = (dict: {[key: string]: number}): {min: number, max: number} => {
  let min = 365;  // This dict holds number of days within a year time.
  let max = 0;

  for(const key in dict) {
    if(dict[key] > max) max = dict[key];
    if(dict[key] < min) min = dict[key];
  }

  return {min, max};
}

export const getQueries = (options?: IParseOptions) => qs.parse(
  history.location.search,
  { ignoreQueryPrefix: true, arrayLimit: Infinity, ...options }
);

// search an object or dictionary for a key/property whose value equal a given value.
export const findKeyByValue = (obj: Record<string, unknown>, val: any) => {
  // Check input vals.
  if(obj == undefined || val == undefined) return undefined;

  // Scan item on key for a matching value.
  for(const key in obj) {
    if(obj[key] === val) {
      return key;
    }
  }
  
  return undefined;
}

export const parseQueries = (queries: string): Queries => qs.parse(queries, { ignoreQueryPrefix: true, interpretNumericEntities: true });

export const getQueriesAsSearch = (queries?: Queries): string =>
  qs.stringify({ ...(queries || getQueries()) }, { addQueryPrefix: true, encode: true });

export const setQueries = (queries: Queries, replace?: boolean, locationArgs?: Partial<LocationDescriptorObject>) => {
  const args = {
    search: qs.stringify(omitBy(queries, (value) => !value), { addQueryPrefix: true }),
    ...locationArgs
  };

  if (replace) {
    return history.replace(args);
  }

  return history.push(args);
};

export const deleteQueries = (queriesToDelete?: Array<string>, replace?: boolean, locationArgs?: Partial<LocationDescriptorObject>) => {
  if (!queriesToDelete) {
    return setQueries({}, replace, locationArgs);
  }

  const currentQueries = getQueries();
  const finalQueries = omitBy(currentQueries, (value, key) => queriesToDelete.includes(key));

  setQueries(finalQueries, replace, locationArgs);
};

// Set the value for the testID such as attribute data-testid=login-submit-btn
export const getTestAttr = (module: string, elementDescription: string, elementName: string) =>
  ({ 'data-testid': `${module}-${elementDescription}-${elementName}` });

// Changed default utc to false to be consistent with Grid
export const formatDate = (date: string | Date, format: string = 'MMM DD, YYYY', utc: boolean = false) => {
  if (!date) {
    return '';
  }
  const retStr = utc ? moment.utc(date).format(format) : moment(date).format(format);
  return retStr;
};

export const getRandomInt = (min: number, max: number) => min + Math.floor(Math.random() * (max - min + 1));

export const getColor = (hue: number, saturation: number = 100, lightness: number = 40, opacity: number = 1): string => {
  const hueRange = 360;

  return `hsla(${hue % hueRange}, ${saturation}%, ${lightness}%, ${opacity})`;
};

export const toLowerCaseFirst = (str: string) => str.charAt(0).toLowerCase() + str.substring(1);

export const parseBytes = (bytes: number, decimals = 2): { amount: number; unit: string } => {
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  if (bytes === 0) {
    return {
      amount: 0,
      unit: units[0]
    };
  }

  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return {
    amount: parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)),
    unit: units[i]
  };
};

export const generateId = () => new Date().getTime();

export const getMaxDate = (items: Array<any>, dateProperty?: string): string => {
  const getItemsMaxDate = (dates: Array<string>) => {
    const normalizedDates = dates.map((date) => new Date(date).getTime());
    const maxItemDate = Math.max(...normalizedDates);

    return new Date(maxItemDate).toISOString();
  };

  if (dateProperty) {
    const itemsDates = items.map((item) => item[dateProperty]);

    return getItemsMaxDate(itemsDates);
  }

  return getItemsMaxDate(items);
};

export const serialize = (data: any) => {
  return JSON.stringify(data);
};

export const deserialize = (str: null | string, defaultValue: any = {}) => {
  try {
    return JSON.parse(str || '');
  } catch {
    return defaultValue;
  }
};

type Key = number | string;

export const getDataFromStorageUnit = (storageKey: Key): { [key: string]: any } => {
  const storageUnit = localStorage.getItem(String(storageKey));

  return deserialize(storageUnit);
};

// Save a key-value set to a local storage set.
export const setDataToStorageUnit = (storageKey: Key, dataKey: Key, data?: any) => {
  // Get existing data.
  const currentData = localStorage.getItem(String(storageKey));
  const deserializedData = deserialize(currentData);

  // Merge new and existing data.
  const nextData = {
    ...deserializedData,
    [dataKey]: data
  };

  // Save updated data to local storage.
  return localStorage.setItem(String(storageKey), serialize(nextData));
};

// Remove a local storage set.
export const deleteStorageUnit = (storageKey: Key) => {
  localStorage.removeItem(String(storageKey));
};

const BREAD_CRUMBS_KEY = 'VI_bread_crumbs';

export const getBreadCrumbs = (key: string): LocationDescriptorObject | undefined => {
  return getDataFromStorageUnit(BREAD_CRUMBS_KEY)[key];
};

export const setBreadCrumbs = (key: string, location: LocationDescriptorObject) => {
  setDataToStorageUnit(BREAD_CRUMBS_KEY, key, location);
};

export const deleteBreadCrumbs = (key: string) => {
  setDataToStorageUnit(BREAD_CRUMBS_KEY, key);
};

// Read a file async.
export const getBase64FromFile = (file: File): Promise<string> => {
  const reader = new FileReader();

  return new Promise((resolve) => {
    // Set the onload handler.
    reader.onload = ({ target }) => {
      if (target) {
        resolve(String(target.result));
      }
    };
    // Read file and trigger onload on completion.
    reader.readAsDataURL(file);
  });
};

//
export const getBase64FromUrl = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve('');

      return;
    }

    const canvas = document.createElement('canvas');
    const canvasCtx = canvas.getContext('2d');
    const img = new Image();

    img.crossOrigin = 'anonymous';

    img.onload = () => {
      canvas.height = img.height;
      canvas.width = img.width;
      canvasCtx?.drawImage(img, 0, 0);

      const dataURL = canvas.toDataURL();

      resolve(dataURL);
    };

    img.onerror = () => {
      reject(`Unexpected errors when loading the image at ${url}.`);
    };

    img.src = url;
  });
};

// Return the first error message from an error object.
export const getDefaultError = (errors: { [key: string]: Array<string> } = {}) => {
  //console.log("*** check error: ", Object.keys(errors));
  let msg = "";
  for (const key of Object.keys(errors)) {
    const values = errors[key]
    //if (values.length > 0) {
      //return '"' + key + '": ' + value[0];
      for(const element of values) {
        //console.log("*** getDefaultError: ", element);
        msg = msg + element + " ";
      }
    //}
  }

  if(msg != "") return msg;

  return 'Server error.';
};

// Check if an obj is iterable.
export function isIterable(value: any) {
  return typeof value === 'object' && value !== null && typeof value[Symbol.iterator] === 'function';
}

// Most portal-api error responses contain a dictionary 'data' with key is object type and value is list of error messages.
// Some other responses have error messages under data.errors. The errors can be an array or a dictionary.
// Convert such response to a string of form "object type: errMsg1. errMsg2. ..."
// Note: expanded to non portal-api errors (eg. AxiosError).
export const errObj2Str = (err: any) => {

  if(err == undefined) {
    return "Portal-API: Unspecified error.";
  }

  // Check if error obj contains a response with a 'data' property.
  if(err.response) {
    const {status, data}= err.response;
    //console.log(`*** Error status: ${status}`, data);

    if(data)
    {
      // Handle errors with msgs stored in 'data.errors' as an array or dictionary. (portal-api)
      if(data.errors) {
        if(Array.isArray(data.errors['error'])) {
          return data.errors['error'].join(". ");
        }
        else if (data.errors.constructor === Object) {
          const texts = Object.keys(data.errors).map(function(key) {
            return data.errors[key];
          });
          const errArr = texts.reduce((acc, curVal) => acc.concat(curVal), []);
          return errArr;
        }
      }

      // Handle errors with msg stored in 'data.error.message' (axiousError from itwins-api).
      if(data.error && data.error.message) {
        return data.error.message;
      }

      // If data is a simple string, return it.
      if(typeof data === 'string') {
        return data;
      }

      // Handle errors with msg stored in 'data'. (portal-api)
      let msg = "";
      for (const key of Object.keys(data)) {
        // Concatenate the err msgs
        const values = data[key];

        if(isIterable(values)) { // Non portal-api errors may not be iterable.
          for(const element of values) {
            msg = `${msg}${element}`;
          }
        }
        else {
          msg = `${msg}${values} `
        }
      }
      if(msg != "") return msg;
    }
  }

  // Default error msg
  return `Portal-API: ${err}`;
};


export const getDayStatus = (date: string) => {
  const extendedDate = moment.utc(date);

  if (extendedDate.isSame(moment().utc(), 'd')) {
    return DayStatus.today;
  }

  if (extendedDate.isSame(moment().utc().add(1, 'days'), 'd')) {
    return DayStatus.tomorrow;
  }

  if (extendedDate.isBefore(moment().utc(), 'd')) {
    return DayStatus.past;
  }

  return DayStatus.default;
};

export const isAfter = (compareToDate: string, comparableDate: string) => {
  return moment.utc(comparableDate).isAfter(compareToDate, 'day');
};


export const getBlockId = (jobRequestName: string) => {
  if(!jobRequestName) return '';
  let longName = jobRequestName;
  // if exists, drop the unique timestamp at the end of the ID (eg. PTI_US-TX1189_1900-1622839188769 => PTI_US-TX1189_1900)
  const lastDash = longName.lastIndexOf("-");
  const lastUnderScore = longName.lastIndexOf("_")
  if(lastUnderScore > -1 && lastDash > lastUnderScore) {
    longName = longName.substring(0, lastDash)
  }

  if(longName.length < 2) {
    return null;
  }
  // get the block number.
  return longName.substring(longName.length - 2);
}

  // Simple function to make sure value read from localStorage is not 'undefined'.
export const readFrLocalStorage = (itemName: string): string => {
    const value = localStorage.getItem(itemName);
    return (value ? value : '');
}

// Store array that keeps the width of grid columns to the local storage.
export const StoreColumnWidthsToLocalStorage = (gridName: string, colWidths: Array<string>): number => {
  if (colWidths && colWidths.length > 0) {
    localStorage.setItem(gridName, JSON.stringify(colWidths));
    return 0;
  }  
  return -1;
}

// return array that keeps the width of grid columns from the local storage.
export const ReadColumnWidthsFrLocalStorage = (gridName: string): Array<string> => {
  const jsonStr = localStorage.getItem(gridName);
  if(jsonStr) {
    try {
      return JSON.parse(jsonStr);
    }
    catch(exc){
      console.log(`****** Could not read column widths for "${gridName}" from localstorage: ${exc}`);
    }
  }

  return [];
}

export const CapFirstLetter = (str: string): string => {
  if(str == undefined || str.length == 0) return str;

  return str.slice(0, 1).toUpperCase() + str.slice(1);
}

export const UnCapFirstLetter = (str: string): string => {
  if(str == undefined || str.length == 0) return str;

  return str.slice(0, 1).toLowerCase() + str.slice(1);
}

// Translate 0 or '0' to blank.
export const Zero2Blank = (val: string | number | undefined): string | number => {

  if(val == undefined) return '';

  let valStr = '';
  if (typeof val === "number") {
    valStr = val.toString();
  } 
  else {
    valStr = val;
  }

  if(valStr === '0' || val === 'None' || val === 'none') {
    return '';
  }
  else {
    return val;
  }
}

export const condStr2Num = (condStr: string): number => {
  if(condStr == undefined || condStr.length == 0 || condStr === '0') return 0;
  return 1;
}

export const str2Num = (str: string) : number => {
  try {
    return Number(str);
  }
  catch {
    return 0;
  }
}

// convert a date to a number of the form yyyymmdd.
export const date2yyyymmdd = (aDate: Date): number => {
  const year = aDate.getFullYear();
  const month = String(aDate.getMonth() + 1).padStart(2, '0');
  const day = String(aDate.getDate()).padStart(2, '0');  

  return parseInt(`${year}${month}${day}`);
}

// Convert a number of the form yyyymmdd to a date.
export const yyyymmdd2date = (dateNumber: number): Date => {
  const year = Math.floor(dateNumber / 10000);
  const month = Math.floor((dateNumber % 10000) / 100) - 1; // Months are zero-based
  const day = dateNumber % 100;
  
  return new Date(year, month, day);
}

// Get indexes of all dimensions for this JobReq from the delis.
export function getRelevantDimensions(delis: Array<SDeliverableInfoDTO | DeliverableDTO>): Array<number> {
  const dims = Array.from(new Set(delis.map(deli => deli.dimensionId))); // Set{1, 1, 1, 2, 3, 3, 4} => {1, 2, 3, 4} 
  return dims;
}

// Clear some persis data (map settings and grid collapse settings).
export const clearPersistData = () => {
  // Clear potentially invalid persist data:
  localStorage.removeItem(MAP_SETTINGS);

  // Note: grid no longer uses these.
  localStorage.removeItem(STATUS_GRID_COLLAPSED_ITEMS);
  localStorage.removeItem(SUMMARY_GRID_COLLAPSED_ITEMS);
  localStorage.removeItem(SITES_GRID_COLLAPSED_ITEMS);
  localStorage.removeItem(TEAM_GRID_COLLAPSED_ITEMS);
  localStorage.removeItem(SHIPPING_GRID_COLLAPSED_ITEMS);
  localStorage.removeItem(CATALOG_GRID_COLLAPSED_ITEMS);
}

// Generate column headings for task/Dimension columns and append to 'headingsInfo'.
//
//  'collectionPercent': {'toolTip':  'Approximate percent complete of stage Collect.', 'label': 'Collect %', 'width': '21', 'visible': {'Pilot': true, 'Observer': true, 'ProjectManager': true}},
//  'collectionAge': {'toolTip':  'Number of days stage Collection has been in progress.', 'label': 'Age', 'width': '14',
export async function getHeadingsInfo(deliverablesStore: DeliverablesStore, headingsInfo: {[id: string]: Record<string, unknown>}): Promise<DimensionDTO[] | undefined> {
  const dims = await deliverablesStore.getDeliverableDimensions();

  if(dims != undefined && dims.length > 0) {
    dims.forEach(dim => {
      const fieldName = normalizeDimensionTypeName(dim.name);

      headingsInfo[`${fieldName}Percent`] = {'toolTip': `Approximate percent complete of stage ${dim.name}.`, 'label': `${dim.name}  %`};
      headingsInfo[`${fieldName}Age`] = {'toolTip': `Number of days stage ${dim.name} has been in progress.`, 'label': `${dim.name} age`};
    });
  }

  return dims;
}

  // Filter and sort deliverables with a given dimension Id.  If Id = 0 return all deliverables.
export function getDeliverablesInfoSorted(dimensionId: number, deliverablesInfo: Array<SDeliverableInfoDTO>): Array<SDeliverableInfoDTO> {
  // Sort by assetName then by deliverableName.
  const matchedItems = deliverablesInfo.filter(item => (dimensionId == 0 || item.dimensionId == dimensionId)).sort(
          (item1, item2) => (item1.step !== item2.step) ? (item1.step - item2.step) : item1.deliverableName > item2.deliverableName ? 1 : -1);
  //console.log("**** in getDeliverableInfo:", matchedItems);
  return matchedItems;
}

// When used as a property, the name must start with lowercase and not contain spaces.
export function normalizeDimensionTypeName(rawName: string): string
{
  let retName: string;

  if(!rawName) return '';

  // "Collect" and "Process" dim name and field name are not the same.  Set manually.
  if(rawName === 'Collect'){
    retName = 'collection';
  }
  else if(rawName === 'Process') {
    retName = 'processing';
  }
  else
  {
    // Make sure first char is lowercase and there are no spaces (possible bug such as "a bc" and "ab c" => "abc").
    retName = (rawName.charAt(0).toLowerCase() + rawName.slice(1)).replace(' ', ''); 
  }

  return retName;
}

// Check if a var is a valid Date.
export function isValidDate(date: unknown, checkRange: boolean = false): boolean {  
  const retVal = date instanceof Date && 
          Object.prototype.toString.call(date) === '[object Date]' &&
          !isNaN(Number(date));

  if(!retVal) return false;

  if(checkRange) {
    const minDate = new Date(2010, 0, 1);
    const maxDate = new Date(2040, 0, 1);
    return date > minDate && date < maxDate;
  }

  return true;
}

// Different in days between two dates (date1 - date2) not considering the time sections.
export const dateDiffInDays = (date1: Date, date2: Date, dbg: boolean = false): number => {

  if(!isValidDate(date1) || !isValidDate(date2)) {
    console.log("****** dateDiffInDays: Invalid date(s).");
    return 0;
  }

  const tmpDate1 = new Date(date1);
  const tmpDate2 = new Date(date2);

  // Drop the time. Only consider the date part.
  tmpDate1.setHours(0, 0, 0, 0);
  tmpDate2.setHours(0, 0, 0, 0);

  const diffMs = tmpDate1.getTime() - tmpDate2.getTime();
  
  if(dbg) {
    console.log("******* dateDiff:", tmpDate1, tmpDate2);
    console.log("******* dateDiff:", tmpDate1.getTime(), " - ", tmpDate2.getTime(), " = ", diffMs, Math.round(diffMs / (1000 * 3600 * 24)));
  }

  return Math.round(diffMs / (1000 * 3600 * 24)); // Convert Ms to days.
}

export const dateDiffInDaysUTC = (date1: Date, date2: Date): number => {
  const tmpDate1 = roundDownToCurrentDayUTC(new Date(date1));
  const tmpDate2 = roundDownToCurrentDayUTC(new Date(date2));  

  const diffMs = tmpDate1.getTime() - tmpDate2.getTime();

  return Math.round(diffMs / (1000 * 3600 * 24)); // Convert Ms to days.
}

// Add days to a Date.
export const addDays = (curDate: Date, days: number, debug: boolean = false): Date => {
  // Alloc a new date with the value of the old date plus number of msecs for the days.  Add 10 secs just to make sure (always add?)
  const newDate = new Date(curDate.getTime() + days * 24 * 3600 * 1000 + 10000); 

  if(debug) {
    console.log("****** addDays:", curDate, days, newDate.getFullYear(), newDate.getMonth() + 1, newDate.getDate(), newDate);
  }

  newDate.setHours(0, 0, 0, 0);  

  return newDate;
}

// Round up a date to the next day.
export function roundUpToNextDayUTC(dateUTC: Date): Date {
  const roundedDateUTC = roundDownToCurrentDayUTC(dateUTC);

  // If more than 5 minutes, round up to the next day.
  if (dateUTC.getUTCHours() > 0 || dateUTC.getUTCMinutes() > 5) {
    roundedDateUTC.setUTCDate(roundedDateUTC.getUTCDate() + 1); // Add 1 day to the UTC date.
  }

  return roundedDateUTC;
}

// Drop the hh:mm:ss part of a date.
export function roundDownToCurrentDayUTC(dateUTC: Date): Date {
  const dateWoTime = new Date(Date.UTC(dateUTC.getUTCFullYear(),dateUTC.getUTCMonth(),dateUTC.getUTCDate(),0,0,0,0));

  //console.log("*** roundDownToCurrentDayUTC:", dateUTC.toISOString(), dateUTC.getUTCDate());

  return dateWoTime;
}

// Function to access date values
export const formatDateAccessor = (field: string, data: any, column: any) => {
  const value = data[field];
  return isValidDate(value) ? new Date(value).toISOString().split("T")[0] : ''; 
};

// Format a date to a string of form 'yyyy-mm-dd' by droping the time part.
export function Date2yyyymmddStr(date: Date): string {
  // Drop the time part.  Note: toISOString is affected by timezone.
  return date.toISOString().split('T')[0].replace(/-/g, ''); 
}

// Format a date to a string of form 'yyyy-mm-dd' by combining the UTC value for yyyy, mm, and dd.
export function Date2UTCyyyymmdd(dateUTC: Date): string {
  const yyyy = dateUTC.getUTCFullYear();
  const mm = String(dateUTC.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-based
  const dd = String(dateUTC.getUTCDate()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}`;
}

export const checkIfDateInList = (inDate: Date, dateArray: Date[]): boolean => {

  const inYear = inDate.getFullYear();
  const inMonth = inDate.getMonth();
  const inDay = inDate.getDate();
  if (dateArray != null) {
    for (const date of dateArray) {
      // Compare the year, month, and day components
      if (date.getFullYear() === inYear && 
          date.getMonth() === inMonth && 
          date.getDate() === inDay) {
        return true; // found a match.
      }
    }
  }
  return false;
}

// Get the last comment for a deliverable from portal-api.
export const getLastDeliverableComment = async (deliverablesStore: DeliverablesStore, deliverableId: number): Promise<{lastComment: string, lastCommentDateTime: Date}> => {
  let retVal = { lastComment: '', lastCommentDateTime: new Date(0)};

  if(deliverableId > 0) {
    await deliverablesStore.commentsStore.getList(deliverableId); // Load the comment list to the store obj.
    const { list } = deliverablesStore.commentsStore; // Extract the list from the store obj.

    // get last item in the list.
    if (list && list.length > 0) {
      retVal = { lastComment: list[list.length - 1].body, lastCommentDateTime: new Date(list[list.length - 1].createdDate) };
    }
  }

  return retVal;
}


  // Form the address by combining the address fields.  Note: there is another copy of this function.
export const formatAddress = (addr: AddressParts): string => {
  let retStr = '';

  retStr = retStr + (addr.addressLine && addr.addressLine.toLowerCase() !== 'unknown' ? addr.addressLine : '_');
  retStr = retStr + ' ' + (addr.city && addr.city.toLowerCase() !== 'unknown' ? addr.city : '_');
  retStr = retStr + ', ' + (addr.state && addr.state.toLowerCase() !== 'unknown' ? addr.state : '_');
  retStr = retStr + ' ' + (addr.zipCode && addr.zipCode.toLowerCase() !== 'unknown' ? addr.zipCode : '_');
  retStr = retStr + ' ' + (addr.country && addr.country.toLowerCase() !== 'unknown' ? addr.country : '_');

  return retStr;
}

// Convert an unknown value to nullable number.
export const any2NullableNum = (val: any): number | undefined => {
  try {
    if(val == undefined || val == null || val.toString().length == 0) return undefined;
    return Number(val);
  }
  catch(err) {
    console.log("*** Exception in any2Num:", err);
  }
  return undefined
}

// Create a user-specific key by append the userId to the key.
export const mkUserKey = (userId: number, keyStr: string): string => {
  return `UID_${userId}_${keyStr}`
}

export const constructionType2name = (t: number | undefined) => {
  return t != undefined && (t in ASSET_CONSTRUCTION_TYPE_LABELS) ? ASSET_CONSTRUCTION_TYPE_LABELS[t] : 
                                                               t ? t.toString() : '';
}

// Check whether all nodes in a treeData structure are selected.
export const Check4CheckAll = (treeData: Array<TaskNode>): boolean => {
  // Count number of selected child nodes.
  const checkedCnt = treeData.reduce((accumulator, t) => {
    // Check if the current item has the specified property with the desired value
    if (t.checked === true) {
        return accumulator + 1; // Increment the count
    } else {
        return accumulator; // No change in count
    }
  }, 0);
  // Count number of parent nodes.
  const parentCnt = treeData.reduce((accumulator, t) => {
    // Check if the current item has the specified property with the desired value
    if (t.hasChildren === true) {
        return accumulator + 1; // Increment the count
    } else {
        return accumulator; // No change in count
    }
  }, 0);

  // Check if all nodes are selected.
  return (checkedCnt + parentCnt == treeData.length);
}

export const convertString2Date = (dateStr: string): Date | undefined => {
  const regexStd = /^\d{4}[-/]\d{2}[-/]\d{2}([ T]\d{2}:\d{2}:\d{2}.*)?$/; // yyyy-mm-ddThh:mm:ss* yyyy/mm/dd hh:mm:ss*
  const regexUS = /^\d{2}[-/]\d{2}[-/]\d{4}([ T]\d{2}:\d{2}:\d{2}.*)?$/;  // mm-dd-yyyy hh:mm:ss
  if(regexStd.test(dateStr) || regexUS.test(dateStr)) {
    return new Date(dateStr);
  }
  else {
    // Just convert then check if the result is valid.
    const date = new Date(dateStr);
    if (date instanceof Date && !isNaN(date.valueOf())) {
      console.log("*** Check date2:", dateStr, new Date(date));
      return date;
    }
    else {
      return undefined;
    }
  }
}

// Add/substrace a number of days to a Date.
// Input:
//   aDate: base date.
//   days: number of days to add.
//   adjustS: number of seconds to adjust.
// TBD: check for problems with Daylight Saving Time.
export function addDaysToDate(aDate: Date, days: number, adjustS: number = 0) {
  const ms = aDate.getTime() + 86400000 * days + adjustS * 1000;

  // let myDate = new Date(aDate);
  // myDate.setDate(myDate.getDate() + 1);

  return new Date(ms);
}

// Get the file extension from a file name.
export function getFileExtension(fileName: string | null | undefined, toLowerCase = true): string {

  if(fileName == undefined || fileName == null || fileName.length == 0) return '';

  const dotIndex = fileName.lastIndexOf('.');
  let ext = dotIndex > -1 ? fileName.substring(dotIndex + 1) : '';
  ext = toLowerCase ? ext.toLowerCase() : ext;

  return ext;
}

// Find duration between two date.  If failed or too short use one day as default.
export function CalculateDuration(endTime: string | Date, startTime: string | Date, defaultVal: 1): number {
  try {
    const tEnd = new Date(endTime);
    const tStart = new Date(startTime);    
    // Find diff in days (rounded up)
    const daysDifference = Math.ceil((tEnd.getTime() - tStart.getTime()) / (1000 * 3600 * 24));

    return daysDifference > 1 ? daysDifference : 1
  } 
  catch (err) {
    console.log("*** Exception in CalculateDuration: ", err);  
    return defaultVal;
  }
}


// Function to search for a node with a given name in a tree structure.
export function searchNodeByDisplayName(root: VirtualNode, name: string): VirtualNode | null {
  // Found a match
  if (root.name === name) {
      return root;
  }

  // Search the children
  if(root.children && root.type === "folder") {
    for (const child of root.children) {
      const result = searchNodeByDisplayName(child, name);
      if (result) {
          return result; // Found in the child subtree
      }
    }
  }

  // Not found.
  return null;
}

// Search for a node with a given name in a tree.
// BUG: node name is not unique.  
export function searchNodeByName(root: VirtualNode, name: string): VirtualNode | undefined {
  // Found a match
  if (root.name === name) {
      return root;
  }

  // Search the children
  if(root.children && root.type === "folder") {
    for (const child of root.children) {
      const result = searchNodeByName(child, name);
      if (result) {
          return result; // Found in the child subtree
      }
    }
  }

  // Not found.
  return undefined;
}

// Search an array of objects for a property with a given value.
export function searchArrayByPropertyValue<T>(array: T[], property: keyof T, value: T[keyof T]): T | undefined {
  if(array == undefined || array.length == 0) return undefined;
  return array.find(item => item[property] === value);
}

export function isExtensionPdf(ext: string): boolean {
  return (['pdf'].findIndex(x => x === ext.toLowerCase()) !== -1);
}

export function isExtensionTiff(ext: string): boolean {
  return (['tif', 'tiff'].findIndex(x => x === ext.toLowerCase()) !== -1);
}

export function isExtensionImage(ext: string): boolean {
  return (['jpg', 'jpeg', 'jpe', 'png'].findIndex(x => x === ext.toLowerCase()) !== -1);
}

export function isExtensionVideo(ext: string): boolean {
  return (['mp4', 'mov'].findIndex(x => x === ext.toLowerCase()) !== -1);
}

export function isExtensionViewable(ext: string): boolean {
  return (['mp4', 'mov', 'pdf', 'jpg', 'jpeg', 'jpe', 'png'].findIndex(x => x === ext.toLowerCase()) !== -1);
}

export function getMIMETypeFromExtension(ext: string): string {
  let type = 'application/octet-stream';
  if (ext.toLowerCase() === 'jpg' || 
      ext.toLowerCase() === 'jpeg' || 
      ext.toLowerCase() === 'jpe') {
    type = 'image/jpeg';
  }
  else if (ext.toLowerCase() === 'png') {
    type = 'image/png';
  }
  else if (ext.toLowerCase() === 'pdf') {
    type = 'application/pdf';
  }
  else if (ext.toLowerCase() === 'mp4' ||
           ext.toLowerCase() === 'mov') {
    type = 'video/mp4';
  }
  return type;
}

// Converts from degrees to radians
function degreesToRadians(d: number): number {
	return d * Math.PI / 180;
}

// Compute the distance in meters between 2 coordinates (in degrees)
export function distanceInMetersFromDegree(latMin: number, latMax: number, lngMin: number, lngMax: number): number {
  const latMinR = degreesToRadians(latMin);
  const lngMinR = degreesToRadians(lngMin);
  const latMaxR = degreesToRadians(latMax);
  const lngMaxR = degreesToRadians(lngMax);

	const diffLat = latMaxR - latMinR;
  const	diffLng = lngMaxR - lngMinR;

	const a = Math.pow(Math.sin(diffLat / 2), 2) + Math.cos(latMinR)*Math.cos(latMaxR) * Math.pow(Math.sin(diffLng/2), 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

	return c * 6371000; // Earth radius in meters
}


// Read file content into a blob.
export function readFileAsBlob(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      const arrayBuffer = fileReader.result as ArrayBuffer;
      const blob = new Blob([arrayBuffer]);
      resolve(blob);
    };

    fileReader.onerror = (error) => {
      reject(error);
    };

    fileReader.readAsArrayBuffer(file); // read file content to an array buffer.
  });
}

// Add a number of items to an array if the items are not already in the array.
export function addNewItems2Array(array: Array<any>, ...items: Array<any>) {
  items.forEach(item => {
  if (item && !array.includes(item)) {
    array.push(item);
  }
  });
}

// function to change the middle of an email address to '...'
export function maskEmail(email: string): string {
  const parts = email.split('@');
  if (parts.length === 2 && parts[0].length > 3) {
    const masked = parts[0].substring(0, 2) + '...' + parts[0].charAt(parts[0].length - 1);
    return masked + '@' + parts[1];
  }
  return email;
}

// Format a list of treeitems to an flat array for showing a 2-level stage-task tree using TreeViewComponent:
// root cells (stages/dimensions) followed by leaf cells (tasks).
// Note: used by Filtering
export const formatFilterTaskTree = (treeItems: TaskTreeItemDTO[], orderTasks: Array<OrderTask> | undefined, forFiltering = false) : Array<any> => {
  const tasksData: Array<any> = [];
  let curParentId = -1;
  if (treeItems) {
    treeItems.forEach(item => {
      // Add stage node.
      if(item.hasChildren) {
        tasksData.push({id: item.id, text: item.text, hasChildren: true});
        curParentId = item.id; // parent Id to use in child nodes.
      }
      // Add task node.
      else {
        if(orderTasks == undefined) // if not defined (ie. new order), check all if not forFiltering and clear all otherwise.
        {
          tasksData.push({id: item.id, parentID: curParentId, text: item.text, checked: !forFiltering});
        }
        else if(orderTasks.some(t => t.delTypeId === item.id)) // if defined (ie. existing order), check only tasks selected for this order.
        {
          tasksData.push({id: item.id, parentID: curParentId, text: item.text, checked: true});
        }
        else
        {
          tasksData.push({id: item.id, parentID: curParentId, text: item.text, checked: false});
        }
      }
    })      
  }

  return tasksData;
}

// Format a 3-level workflow data from portal-api to a flat array for TreeViewComponent.
function format3LevelWorkFlow(workflow: TaskTreeItemDTO, 
  orderTasks: Array<OrderTask> | undefined = undefined): 
  Array<TaskNode> {

  const tasksData: Array<TaskNode> = [];

  if(workflow)  {
    // Add current node.
    if(workflow.hasChildren) { // workflow and stage/dimension node.
      if(workflow.parentID) { // stage/dimension nodes which have parent.
        tasksData.push({id: workflow.id, parentID: workflow.parentID, text: workflow.text, hasChildren: workflow.hasChildren});            	  
      }
      else { // workflow node
        tasksData.push({id: workflow.id, text: workflow.text, hasChildren: workflow.hasChildren});
      }
      //console.log("***: ", workflow.text, " and children: ", workflow.children?.length);
      // Add current node's children recursively.
      if(workflow.children && workflow.children.length > 0) {
        workflow.children.forEach(child => {
          const children = format3LevelWorkFlow(child, orderTasks);
          tasksData.push(...children);
        });
      }
    }
    else { // leaf/task node.
      if(orderTasks && orderTasks.length > 0) { // editing case.
        if(orderTasks.some(t => t.delTypeId === workflow.id)) // 
        {
          tasksData.push({id: workflow.id, parentID: workflow.parentID, text: workflow.text, hasChildren: workflow.hasChildren, checked: true});
        }
        else
        {
          tasksData.push({id: workflow.id, parentID: workflow.parentID, text: workflow.text, hasChildren: workflow.hasChildren, checked: false});
        }
      }
      else { // new order case.
        tasksData.push({id: workflow.id, parentID: workflow.parentID, text: workflow.text, hasChildren: workflow.hasChildren, checked: workflow.checked});
      }
    }
  }

  return tasksData;
}

// Format the 3-level task tree data to a list of flat arrays for TreeViewComponent.
export function FormatWorkFlows(treeItems: TaskTreeItemDTO[], orderTasks: Array<OrderTask> | undefined = undefined): 
                Array<Array<TaskNode>> {

  const workflows: Array<Array<TaskNode>> = [];

  if(treeItems)  {
    // Iterate the top level nodes (workflows)
    treeItems.forEach(item => {
      workflows.push(format3LevelWorkFlow(item, orderTasks));
    });
  }

  return workflows;
}

// Make sure startDate is valid and not greater than endDate.
// If not, set startDate to endDate - 1 day.
// If endDate is also invalid, set both to current date.
function normalizeStartDate(siteData: Map<string, Array<ScheduleBarSegment>>) {
  const maxDate = new Date(2050, 0, 1);

  siteData.forEach((value) => {
    value.forEach((item) => {
      if(item.EndDate && isValidDate(item.EndDate, true)) { // EndDate is DueDate and should be set.
        if(!isValidDate(item.StartDate, true) || // If start date is invalid or
           item.StartDate > item.EndDate) { // greater than end date set it to end date - 1 day.
          const newDate = new Date(item.EndDate);
          newDate.setDate(newDate.getDate() - 1);
          item.StartDate = newDate;
        }

        item.Duration = Math.max(1, dateDiffInDays(item.EndDate, item.StartDate));
      }
      else {
        // If end date is invalid, set both to an unsually large date to be filtered out later.
        item.StartDate = maxDate;
        item.EndDate = maxDate;
        item.Duration = 0;
      }
    });
  });
}

// Create a timeline node (ie. baseline, forecast, or actual) with its stage nodes.
function formatStageData(stageInfo: Array<ScheduleBarSegment>, timelineName: string, baseId: number): ScheduleNode {
  
  // Create a baseline/forecast/actual node.  
  const timelineNode: ScheduleNode = {
    NodeID: baseId,
    NodeName: timelineName,
    StartDate: new Date(2050, 0, 1),
    EndDate: new Date(0),
    Progress: 100,
    SubNodes: [],
    NodeType: 20000
  }

  // Add the stage nodes.
  let nodeId = baseId * 100; // Assume there are less than 100 tasks in a stage.  Otherwise, there may be duplicate NodeIDs.

  for (let i = 0; i < stageInfo.length; i++) {
    const stageNode: ScheduleNode = {
      NodeID: nodeId++,
      NodeName: StageId2Name[i],
      StartDate: stageInfo[i].StartDate,
      Duration: stageInfo[i].Duration,
      Progress: 100,
      SubNodes: [],
      NodeType: i
    }

    if(stageInfo[i].StartDate < timelineNode.StartDate) {
      timelineNode.StartDate = stageInfo[i].StartDate;
    }
    if(isValidDate(stageInfo[i].EndDate, true) &&
       stageInfo[i].EndDate > timelineNode.EndDate) {
      timelineNode.EndDate = stageInfo[i].EndDate;
    }

    timelineNode.SubNodes?.push(stageNode);
  }

  return timelineNode;
}

// Format the flat deliverable list into a 3-level tree structure.
export function formatDeliverableList(deliverablesInfo: Array<SDeliverableInfoDTO>): 
                Array<ScheduleNode> {

  const treeItems: Array<ScheduleNode> = [];

  const siteBaseline = new Map<string, Array<ScheduleBarSegment>>();
  const siteForecast = new Map<string, Array<ScheduleBarSegment>>();
  const siteActual = new Map<string, Array<ScheduleBarSegment>>(); 

  const defStartDate = new Date(2050, 0, 1);
  const defEndDate = new Date(0);

  if(deliverablesInfo)  {
    deliverablesInfo.forEach(item => {
      //console.log("*** item: ", item.name, item);

      // Check and add bars for a site.
      //const curSite = item.name.toLowerCase();
      const curSite = item.name;
      if(!siteBaseline.has(curSite)) {
        siteBaseline.set(curSite, [
          // indexes corresponding to STAGE_IDX enum.
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
        ]);
        siteForecast.set(curSite, [
          // indexes corresponding to STAGE_IDX enum.
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
        ]);
        siteActual.set(curSite, [
          // indexes corresponding to STAGE_IDX enum.
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
          { StartDate: defStartDate, EndDate: defEndDate, Duration: 0},
        ]);
      }

      const startBaseline = item.planStartDate;
      const endBaseline = item.dueDate;

      const startForecast = item.forecastStartDate;
      const endForecast = item.forecastCompletionDate;

      const startActual = isValidDate(item.startDate) ? item.startDate : // startDate could be undefined.
                          isValidDate(item.actualCompletionDate) ? item.actualCompletionDate : item.dueDate; 
      const endActual = isValidDate(item.actualCompletionDate) ? item.actualCompletionDate : item.dueDate;

      let stageIdx: number = -1;

      // Check and get the stage index for this task/deli.
      switch (item.dimensionId) {
        case 2:
        case 3: 
          stageIdx = STAGE_IDX.Collect;
          break;        
        case 4:
          stageIdx = STAGE_IDX.Production;
          break;        
        case 5:
          stageIdx = STAGE_IDX.AI;
          break;        
        case 10:
          stageIdx = STAGE_IDX.Report;
          break;        
        case 11:
          stageIdx = STAGE_IDX.Accept;
          break;        
        default:
          console.log("********** Unhandled dimensionId: ", item.dimensionId);
          break;
      }

      //console.log("********** idx:", stageIdx);
      // Set the start/end dates for the stage from the max/min of its tasks.
      if(stageIdx > -1) {
        // Stage baseline.
        let segmentArray = siteBaseline.get(curSite);
        if(segmentArray && startBaseline < segmentArray[stageIdx].StartDate) {
          segmentArray[stageIdx].StartDate = startBaseline;
        }
        if(segmentArray && 
          segmentArray[stageIdx] &&
          segmentArray[stageIdx].EndDate &&
          endBaseline > segmentArray[stageIdx].EndDate) {
            segmentArray[stageIdx].EndDate = endBaseline;
        }

        // Stage forecast.
        segmentArray = siteForecast.get(curSite);
        if(segmentArray && startForecast < segmentArray[stageIdx].StartDate) {
          segmentArray[stageIdx].StartDate = startForecast;
        }
        if(segmentArray &&
          segmentArray[stageIdx] &&
          segmentArray[stageIdx].EndDate && 
          endForecast > segmentArray[stageIdx].EndDate) {
            segmentArray[stageIdx].EndDate = endForecast;
        }

        // Stage actual.
        segmentArray = siteActual.get(curSite);
        if(segmentArray &&
          startActual < segmentArray[stageIdx].StartDate) {
            segmentArray[stageIdx].StartDate = startActual;
        }
        if(segmentArray &&
          segmentArray[stageIdx] &&
          segmentArray[stageIdx].EndDate && 
          endActual > segmentArray[stageIdx].EndDate) {
            segmentArray[stageIdx].EndDate = endActual;
        }
      }
      // console.log("********** siteBaseline:", startBaseline, endBaseline, siteBaseline[curSite][stageIdx]);
      // console.log("********** siteForecast:", startForecast, endForecast, siteForecast[curSite][stageIdx]);
      // console.log("********** siteActual:", startActual, endActual, siteActual[curSite][stageIdx]);
    });
  }

  normalizeStartDate(siteBaseline);
  normalizeStartDate(siteForecast);
  normalizeStartDate(siteActual);

  //console.log("********** CHECK1:", siteBaseline, siteForecast, siteActual);

  // Create tree for each site.
  const siteList = Array.from(siteBaseline.keys());
  let siteIdx = 1;

  siteList.forEach(siteName => {
    // Create a site node such as "borgiallo"
    const siteNode: ScheduleNode =  {
      NodeID: siteIdx++,
      NodeName: siteName,
      StartDate: new Date(),
      Duration: 30,
      Progress: 100,
      SubNodes: [],
      NodeType: 10000
    }

    let startDate = new Date(2050, 0, 1);
    let endDate = new Date(0);

    // Add the baseline node.
    const baselineNode = formatStageData(siteBaseline.get(siteName) as ScheduleBarSegment[], "Baseline", siteIdx++);
    siteNode.SubNodes?.push(baselineNode);

    // use max/min of dates for start/end dates
    if(baselineNode.StartDate < startDate) {
      startDate = baselineNode.StartDate;
    }
    if(baselineNode.EndDate &&
       isValidDate(baselineNode.EndDate, true) &&
       baselineNode.EndDate > endDate) {
      endDate = baselineNode.EndDate;
    }

    // Add the forecast node.
    const forecastNode = formatStageData(siteForecast.get(siteName) as ScheduleBarSegment[], "Forecast", siteIdx++);
    siteNode.SubNodes?.push(forecastNode);

    // use max/min of dates for start/end dates
    if(forecastNode.StartDate < startDate) {
      startDate = forecastNode.StartDate;
    }
    if(forecastNode.EndDate && // not needed, just to avoid lint warning.
       isValidDate(forecastNode.EndDate, true) &&
       forecastNode.EndDate > endDate) {
      endDate = forecastNode.EndDate;
    }

    // Add the actual node.
    const actualNode = formatStageData(siteActual.get(siteName) as ScheduleBarSegment[], "Actual", siteIdx++);
    siteNode.SubNodes?.push(actualNode);

    // use max/min of dates for start/end dates
    if(actualNode.StartDate < startDate) {
      startDate = actualNode.StartDate;
    }
    if(actualNode.EndDate &&
       isValidDate(actualNode.EndDate, true) &&
       actualNode.EndDate > endDate) {
      endDate = actualNode.EndDate;
    }

    siteNode.StartDate = startDate;
    siteNode.Duration = dateDiffInDays(endDate, startDate);

    treeItems.push(siteNode);
  });

  return treeItems;
}

// Get list of first level nodes (site nodes) from a tree structure.
export function getSiteNodeList(treeItems: Array<ScheduleNode>): Array<ScheduleNode> {
  const siteNodes: Array<ScheduleNode> = [];

  // Add a site node for each top level node.
  treeItems.forEach(item => {
    if(item.NodeType === 10000) {
      const siteNode: ScheduleNode =  {        
        NodeID: item.NodeID,
        NodeName: item.NodeName,
        StartDate: item.StartDate,
        Duration: item.Duration,
        Progress: item.Progress,
        NodeType: 10000,
        ExpandState: 'collapsed'
      }

      siteNodes.push(siteNode);
      // Add a dummy node so the site node can be expanded.
      siteNodes.push(makeDummyNode(item));
    }
  });

  return siteNodes;
}

// Create a dummy node for a site node to make sure the site node has a child node.
function makeDummyNode(item: ScheduleNode): ScheduleNode {
  return {
    ParentID: item.NodeID,
    NodeID: item.NodeID * 100,
    NodeName: '.',
    StartDate: item.StartDate,
    Duration: 1,
    Progress: 100,
    NodeType: 30000,
    ExpandState: 'collapsed'
};
}

// Flatten the tree structure to a flat array for GanttComponent.
// The component loads faster on a flat array.
export function flattenTreeItems(parent: ScheduleNode | undefined, treeItems: Array<ScheduleNode>, is2Level: boolean = false): Array<ScheduleNode> {
  const flatNodes: Array<ScheduleNode> = [];

  // if(parent) {
  //   console.log("*** parent is null:", treeItems);
  // }

  treeItems.forEach(item => {
    const hasSubNodes = item.SubNodes && item.SubNodes.length > 0;

    const startDate = isValidDate(item.StartDate, true) ? item.StartDate : undefined;
    const endDate = isValidDate(item.EndDate, true) ? item.EndDate : undefined;
    let duration = item.Duration == undefined ? 1000000 : item.Duration;

    if(duration >= 1000000) {
      if(isValidDate(startDate) && isValidDate(endDate)) {
        duration = dateDiffInDays(endDate as Date, startDate as Date);
      }
      else {
        duration = 0;
      }
    }

    if(item.NodeType === 10000) { // Site node.
      flatNodes.push({
        NodeID: item.NodeID,
        NodeName: item.NodeName,
        StartDate: startDate,
        Duration: duration,
        Progress: item.Progress,
        NodeType: item.NodeType,
        ExpandState: 'collapsed'
      });
    }
    else if(item.NodeType === 20000) { // baseline/forecast/actual node.
      flatNodes.push({
        NodeID: item.NodeID,
        NodeName: item.NodeName,
        StartDate: startDate,
        EndDate: endDate,
        Duration: duration,
        Progress: item.Progress,
        NodeType: item.NodeType,
        ParentID: parent? parent.NodeID : -1,
        ExpandState: 'collapsed'
      });
    }
    else if(!is2Level) {                          // stage node if need more than 2 levels.
      flatNodes.push({
        NodeID: item.NodeID,
        NodeName: item.NodeName,
        StartDate: startDate,
        Duration: duration,
        Progress: item.Progress,
        NodeType: item.NodeType,
        ParentID: parent? parent.NodeID : -1
      });
    }

    if(hasSubNodes) {
      const subNodes = flattenTreeItems(item, item.SubNodes as ScheduleNode[], is2Level);
      flatNodes.push(...subNodes);
    }
  });

  return flatNodes;
}

// Get a page at a given index from the tree structure.
export function getPageFromTreeItems(pageIndex: number, pageSize: number, treeItems: Array<ScheduleNode>, is2Level: boolean = false): Array<ScheduleNode> {
  const pageNodes: Array<ScheduleNode> = [];

  // Invalid index.
  if(pageIndex < 0) return [];

  const skip = pageIndex * pageSize;
  // Index out of range.
  if(skip > treeItems.length) return [];

  const numOfItems = Math.min(treeItems.length - skip, pageSize);

  // Jump to the page boundary and adding the site nodes.
  for(let i = skip; i < (skip + numOfItems); i++)
  {
    // Add the site node.
    const item = treeItems[i];
    pageNodes.push({
      NodeID: item.NodeID,
      NodeName: item.NodeName,
      StartDate: item.StartDate,
      Duration: item.Duration,
      Progress: item.Progress,
      NodeType: item.NodeType,
      ExpandState: 'collapsed'
    });

    // Add the child nodes (recursive function).
    const subNodes = flattenTreeItems(item, item.SubNodes as ScheduleNode[], is2Level);
    pageNodes.push(...subNodes);
  }

  return pageNodes;
}

// Get all decendant leaf nodes of a node.
export function getAllLeafNodes(curNode: TaskTreeItemDTO): Array<TaskTreeItemDTO> {
  const leafNodes: Array<TaskTreeItemDTO> = [];

  if(curNode.children && curNode.children.length > 0) {
    // Iterate on the children.
    curNode.children.forEach(item => {
      const hasChildren = item.children && item.children.length > 0;

      // Recursively on grand children.
      if(hasChildren) {
        const children = getAllLeafNodes(item);
        leafNodes.push(...children);
      }
      // Add the leaf node.
      else {
        leafNodes.push(item);
      }
    });
  }

  return leafNodes;
}

// Find a stage node (2nd level) in a 3-level tree structure.
// The 3-level tree structure has this format:
//   - Workflows with ID 900000 + n where n is the 1-based index of the workflow.
//     - Stages with ID 10000 * n + m where m is the 1-based index of the stage. 
//       - Tasks with ID less than 10000 and shared between workflows.
export function findStageNode(stageNodeID: number, treeItems: Array<TaskTreeItemDTO>): TaskTreeItemDTO | undefined {
  let foundNode: TaskTreeItemDTO | undefined = undefined;

  if(stageNodeID < 10000) return undefined;

  const workflowIdx = Math.floor(stageNodeID / 10000) - 1;

  if(treeItems.length > workflowIdx && 
    treeItems[workflowIdx].children &&
    treeItems[workflowIdx].children.length > 0) {
    foundNode = treeItems[workflowIdx].children.find(item => item.id == stageNodeID);
    console.log("*** FindStageNode1:", stageNodeID, workflowIdx, treeItems[workflowIdx].children.map(x => x.id), foundNode);
  }
  else {
    treeItems.forEach(item => {
      if(item.children && item.children.length > 0) {
        foundNode = item.children.find(item => item.id === stageNodeID);
        console.log("*** FindStageNode2:", stageNodeID, item, foundNode);
      }
    });
  }

  return foundNode;
}

// Update the checked status of items in all workflows.
export function setEffectedNodes(workflows: Array<Array<TaskNode>>, 
                                 affectedNodes: Array<number>, // List of node IDs to be checked/unchecked.
                                 checked: boolean): // Whether to check or uncheck the nodes.
        Array<Array<TaskNode>> {
  
  // Make a deep copy of the workflows.
  const newWorkflows = structuredClone(workflows);

  newWorkflows.forEach(workflow => {
    //console.log("*** cur workflow:", workflow.map(x => x.id));
    workflow.forEach(item => {
      if(affectedNodes.includes(item.id)) {
        //console.log("*** flip:", item, affectedNodes, checked);
        item.checked = checked;
      }
    });
  });

  return newWorkflows;
}


export function NormalizeStartNEndDate(startDate: Date, endDate: Date): [Date, Date] {
  if(startDate > endDate) {
    return [endDate, startDate];
  }
  return [startDate, endDate];
}

// Build a struct to show a taskbar for each site based on its deliverables.
// Format the flat deliverable list into a 3-level tree structure.
export function getSiteInfoFromDeliverableList(deliverablesInfo: Array<SDeliverableInfoDTO>): 
                Array<ScheduleSiteInfo> {

  const siteBars: Array<ScheduleSiteInfo> = [];

  const siteDelis = new Map<string, DeliverablesSummary>(); // Key is sitename, value is site deliverable summary.

  // Default start/end dates.
  const defStartDate = new Date(2050, 0, 1);
  const defEndDate = new Date(0);

  const endOfToday = roundUpToNextDayUTC(new Date());
  //const startOfToday = roundDownToCurrentDayUTC(new Date());

  if(deliverablesInfo) {
    deliverablesInfo.forEach(item => {
      //console.log("*** item: ", item.name, item);
      const siteName = item.name;

      // Add a new site (ie. key) if the siteName is not in the Map.
      if(!siteDelis.has(siteName)) {
        siteDelis.set(siteName, {
          SiteId: item.serialNumber,
          DeliverableCnt: 0,
          CompleteCnt: 0,
          PendingCnt: 0,
          PlanStartDate: defStartDate,
          PlanCompletionDate: defEndDate,
          ForecastStartDate: defStartDate,
          ForecastCompletionDate: defEndDate,
          ActualStartDate: defStartDate,
          ActualCompletionDate: defEndDate,
          WorseLateVariance: Number.MIN_SAFE_INTEGER,
          LastOnGoingVariance: Number.MAX_SAFE_INTEGER,
          LastPlanEndVariance: Number.MIN_SAFE_INTEGER,
        });
      }

      // Set up the site deliverable summary (ie. aggregate data corresponding to a site/key).
      const siteDeliInfo = siteDelis.get(siteName);
      if(siteDeliInfo) {
        const startBaseline = isValidDate(item.planStartDate) ? item.planStartDate : defStartDate;
        const endBaseline = isValidDate(item.dueDate) ? item.dueDate : defEndDate; // dueDate is planEndDate.
      
        const startForecast = isValidDate(item.forecastStartDate) ? item.forecastStartDate : defStartDate;
        const endForecast = isValidDate(item.forecastCompletionDate) ? item.forecastCompletionDate : defEndDate;

        const startActual = isValidDate(item.startDate) ? item.startDate : defStartDate; // startDate is actualStartDate.
        const endActual = isValidDate(item.actualCompletionDate) ? item.actualCompletionDate : defEndDate;

        // Get the counters for total/complete/pending deliverables.
        siteDeliInfo.DeliverableCnt++;
        if(item.completionStatus == 4 ||  // Completed.
          item.completionStatus == 5  ||  // Failed.
          item.completionStatus == 13 ||  // Completed with issues.
          isValidDate(item.actualCompletionDate, true)) { // Status not set, but ActualEnd has value.
            siteDeliInfo.CompleteCnt++;    // All above cases are considered as completed. 
        }
        else {
          // Keep count of pending deliverables to check for jobs that have not been started.
          if(item.completionStatus == 1) {
            siteDeliInfo.PendingCnt++;  
          }

          // Keep the worse variance for late tasks.
          if(endBaseline.getTime() != defEndDate.getTime() && 
              endActual.getTime() == defEndDate.getTime()) { // Sometimes completionStatus is not uptodate.  Verify that endActual is invalid.

            if(endBaseline < endOfToday) { // Late tasks.  DueDate is in the past.
              const varInt = dateDiffInDays(endOfToday, endBaseline);
              if(varInt > siteDeliInfo.WorseLateVariance) {
                siteDeliInfo.WorseLateVariance = varInt;
                siteDeliInfo.LateVarianceNote = [`Relevant task: ${item.deliverableName}`,
                                                `    Plan:  ${Date2UTCyyyymmdd(endBaseline)}`, // Use Unicode spaces U+00A0 cause html groups regular spaces.
                                                `    Today: ${Date2UTCyyyymmdd(endOfToday)}`];
              }
              console.log("*********** check WorseLateVariance:", siteName, item.deliverableName, endBaseline.toISOString(), endOfToday.toISOString(), varInt, siteDeliInfo.WorseLateVariance);
            }
            else { // On going tasks.
              const varInt = dateDiffInDaysUTC(endBaseline, endOfToday);
              if(varInt < siteDeliInfo.LastOnGoingVariance) {
                siteDeliInfo.LastOnGoingVariance = varInt;
                siteDeliInfo.OnGoingVarianceNote = [`Relevant task: ${item.deliverableName}`,
                                                  `    Plan:  ${Date2UTCyyyymmdd(endBaseline)}`, // Use Unicode spaces U+00A0 cause html groups regular spaces.
                                                  `    Today: ${Date2UTCyyyymmdd(endOfToday)}`];
              }
            }
          }
        }  
        
        // const startActual = isValidDate(item.startDate) ? item.startDate : // startDate could be undefined.
        //                     isValidDate(item.actualCompletionDate) ? item.actualCompletionDate : item.dueDate; 
        
        // Store the min/max of start/end dates from all delis.  This should filter out the default dates.
        //
        // Baseline dates.
        if(startBaseline < siteDeliInfo.PlanStartDate) {
          siteDeliInfo.PlanStartDate = startBaseline;
        }
        if(isValidDate(siteDeliInfo.PlanCompletionDate) &&
          endBaseline > siteDeliInfo.PlanCompletionDate) {
          siteDeliInfo.PlanCompletionDate = endBaseline;

          // Calculate the variance from the latest completed task if any.
          if(isValidDate(endActual, true) && isValidDate(endBaseline, true)) { 
            siteDeliInfo.LastPlanEndVariance = dateDiffInDaysUTC(endActual, endBaseline);
            siteDeliInfo.PlanEndVarianceNote = [`Relevant task: ${item.deliverableName}`, 
                                                `    Plan:   ${Date2UTCyyyymmdd(endBaseline)}`, // Use Unicode spaces U+00A0 cause html groups regular spaces.
                                                `    Actual: ${Date2UTCyyyymmdd(endActual)}`];
            console.log("****** check lastPlanEndVariance:", siteName, endActual.toISOString(), endBaseline.toISOString(), siteDeliInfo.LastPlanEndVariance);
          }
        }

        // Forecast dates
        if(startForecast < siteDeliInfo.ForecastStartDate) {
          siteDeliInfo.ForecastStartDate = startForecast;
        }
        if(endForecast > siteDeliInfo.ForecastCompletionDate) {
          siteDeliInfo.ForecastCompletionDate = endForecast;
        }

        // Actual dates.
        if(startActual < siteDeliInfo.ActualStartDate) {
          siteDeliInfo.ActualStartDate = startActual;
        }
        if(endActual > siteDeliInfo.ActualCompletionDate) {
          siteDeliInfo.ActualCompletionDate = endActual;
        }
      }
    });
  }

  console.log("*** Site Delis map:", siteDelis);

  // Build bar data for each site from the siteDelis map.
  const siteNameList = Array.from(siteDelis.keys());
  siteNameList.forEach(siteName => {
    const curDeli = siteDelis.get(siteName);
    if(curDeli && curDeli.DeliverableCnt) {
      // Create a site node such as "borgiallo"
      const siteBarData: ScheduleSiteInfo =  {
        SiteId: curDeli.SiteId,
        SiteName: `${siteName} (${curDeli.SiteId})`, // show SiteId after name.
        Progress: 0,
      }

      if(curDeli.CompleteCnt > 0) { // Some or all delis have been completed.
        siteBarData.StartDate = roundDownToCurrentDayUTC(curDeli.ActualStartDate); // Start date is the actual start date.
        if(curDeli.CompleteCnt == curDeli.DeliverableCnt) { // All delis have been completed.  
          // Use actual completion date for endDate.
          siteBarData.EndDate = roundUpToNextDayUTC(curDeli.ActualCompletionDate);
          siteBarData.Progress = 100;
        }
        else { // On going, use ForecastCompletionDate for endDate.
          siteBarData.EndDate = roundUpToNextDayUTC(curDeli.ForecastCompletionDate);
          siteBarData.Progress = Math.floor(100 * curDeli.CompleteCnt / curDeli.DeliverableCnt);
        }
      }
      else if(curDeli.PendingCnt == curDeli.DeliverableCnt) { // All delis are pending.
        siteBarData.StartDate = roundDownToCurrentDayUTC(curDeli.PlanStartDate);
        siteBarData.EndDate = roundUpToNextDayUTC(curDeli.PlanCompletionDate);
        siteBarData.Progress = 0;
      }
      else { // No completions, but some delis are in progress.
        siteBarData.StartDate = roundDownToCurrentDayUTC(curDeli.ActualStartDate);
        siteBarData.EndDate = roundUpToNextDayUTC(curDeli.ForecastCompletionDate);
        siteBarData.Progress = 5; // Set some small value to show the site is in progress.
      }

      // Calculate the duration.
      siteBarData.Duration = dateDiffInDays(siteBarData.EndDate as Date, siteBarData.StartDate as Date);

      // Set the baseline dates.
      siteBarData.BaselineStartDate = roundDownToCurrentDayUTC(curDeli.PlanStartDate);
      siteBarData.BaselineEndDate = roundUpToNextDayUTC(curDeli.PlanCompletionDate);

      // Calculate the variance.
      //const varInt = dateDiffInDays(siteBarData.EndDate as Date, siteBarData.BaselineEndDate as Date);
      //siteBarData.Variance = varInt > 0 ? '+' + varInt.toString() : varInt.toString();

      if(curDeli.WorseLateVariance == Number.MIN_SAFE_INTEGER) { // No late tasks.
        if(curDeli.LastPlanEndVariance != Number.MIN_SAFE_INTEGER) { 
          if(curDeli.LastOnGoingVariance != Number.MAX_SAFE_INTEGER && // Use LastOnGoingVariance if it's smaller than LastPlanEndVariance.
            curDeli.LastOnGoingVariance < curDeli.LastPlanEndVariance) { 
            siteBarData.Variance = curDeli.LastOnGoingVariance > 0 ? '+' + curDeli.LastOnGoingVariance.toString() : curDeli.LastOnGoingVariance.toString();
            siteBarData.VarianceDescription = curDeli.OnGoingVarianceNote;
          }
          else { // Otherwise, use LastPlanEndVariance.
            siteBarData.Variance = curDeli.LastPlanEndVariance > 0 ? '+' + curDeli.LastPlanEndVariance.toString() : curDeli.LastPlanEndVariance.toString();
            siteBarData.VarianceDescription = curDeli.PlanEndVarianceNote;
          }
        }
        else {
          // If no complete tasks, but some are in progress, use LastOnGoingVariance.
          if(curDeli.LastOnGoingVariance != Number.MAX_SAFE_INTEGER) {
            siteBarData.Variance = curDeli.LastOnGoingVariance > 0 ? '+' + curDeli.LastOnGoingVariance.toString() : curDeli.LastOnGoingVariance.toString();
            siteBarData.VarianceDescription = curDeli.OnGoingVarianceNote;
          }
          else { 
            siteBarData.Variance = undefined;
            siteBarData.VarianceDescription = undefined;
          }
        }
      }
      else {       
        siteBarData.Variance = curDeli.WorseLateVariance > 0 ? '+' + curDeli.WorseLateVariance.toString() : curDeli.WorseLateVariance.toString();
        siteBarData.VarianceDescription = curDeli.LateVarianceNote;
      }
      console.log("*** SiteBarData:", siteName, siteBarData);

      siteBars.push(siteBarData);
    }
  });

  return siteBars;
}

// Get a page with a given number of items at a given index from a list.
export function getPageFromItemList<T>(pageIndex: number, pageSize: number, itemList: Array<T>): Array<T> {

  // Invalid index.
  if(pageIndex < 0) return [];

  const skip = pageIndex * pageSize;
  // Index out of range.
  if(skip > itemList.length) return [];

  const numOfItems = Math.min(itemList.length - skip, pageSize);

  return itemList.slice(skip, skip + numOfItems);
}

// Check for invalid dates and try to adjust to reasonable values.
export function fixUpStartNEndDate(startDate: Date, endDate: Date): [Date, Date] {

  if(!isValidDate(startDate, true)) {
    if(!isValidDate(endDate, true)) { // Both dates are invalid.  Set both to current date.
      startDate = roundDownToCurrentDayUTC(new Date());
      endDate = startDate;
    }
    else { // EndDate is valid.  Use it for both.
      startDate = endDate;
    }
  }
  else if(!isValidDate(endDate, true)) { // EndDate is invalid.  Use StartDate for both.
    endDate = startDate;
  }

  return [startDate, endDate];
}

// Construct the struct for a site timeline bar its asset schedule info.
export function scheduleSiteInfoFrAsset(asset: OrderAssetDTO): ScheduleSiteInfo {

  const [startDate, endDate] = fixUpStartNEndDate(roundDownToCurrentDayUTC(new Date(asset.scheduleInfo.startDate)),
                                                  roundUpToNextDayUTC(new Date(asset.scheduleInfo.endDate)));
  const duration = dateDiffInDays(endDate, startDate);

  const [baselineStartDate, baselineEndDate] = fixUpStartNEndDate(roundDownToCurrentDayUTC(new Date(asset.scheduleInfo.baselineStart)),
                                                            roundUpToNextDayUTC(new Date(asset.scheduleInfo.baselineEnd)));

  const siteInfo: ScheduleSiteInfo = {
    SiteId: asset.serialNumber,
    SiteName: `${asset.name} (${asset.serialNumber})`, // show SiteId after name.
    Progress: asset.deliverables.length > 0 ? Math.ceil(asset.scheduleInfo.completeCnt / asset.deliverables.length * 100) : 0,
    StartDate: startDate,
    EndDate: endDate,
    Duration: duration,
    BaselineStartDate: baselineStartDate,
    BaselineEndDate: baselineEndDate,
    Variance: asset.scheduleInfo.variance,
    VarianceDescription: asset.scheduleInfo.notes
  }

  return siteInfo;
}